import React, { useRef, useEffect } from 'react';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, AlignLeft, AlignCenter, AlignRight, AlignJustify } from 'lucide-react';

const RichTextEditor = ({ value, onChange, placeholder, label, minHeight = '100px' }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const createLink = () => {
    const url = prompt('Enter the URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  return (
    <div className="space-y-2">
      {label && <Label className="text-slate-700">{label}</Label>}
      
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-slate-100 border border-slate-300 rounded-t-md">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('underline')}
          title="Underline (Ctrl+U)"
        >
          <Underline className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-8 bg-slate-300 mx-1" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('insertUnorderedList')}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('insertOrderedList')}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-8 bg-slate-300 mx-1" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={createLink}
          title="Insert Link"
        >
          <LinkIcon className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-8 bg-slate-300 mx-1" />
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('justifyLeft')}
          title="Align Left"
        >
          <AlignLeft className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('justifyCenter')}
          title="Align Center"
        >
          <AlignCenter className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('justifyRight')}
          title="Align Right"
        >
          <AlignRight className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => execCommand('justifyFull')}
          title="Justify"
        >
          <AlignJustify className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-8 bg-slate-300 mx-1" />
        
        <select
          className="h-8 px-2 text-sm border border-slate-300 rounded"
          onChange={(e) => execCommand('formatBlock', e.target.value)}
          defaultValue=""
        >
          <option value="">Paragraph</option>
          <option value="<h1>">Heading 1</option>
          <option value="<h2>">Heading 2</option>
          <option value="<h3>">Heading 3</option>
        </select>
        
        <input
          type="color"
          className="h-8 w-12 border border-slate-300 rounded cursor-pointer"
          onChange={(e) => execCommand('foreColor', e.target.value)}
          title="Text Color"
        />
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="w-full px-3 py-2 border border-slate-300 rounded-b-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-auto"
        style={{ minHeight }}
        data-placeholder={placeholder}
      />

      <style>{`
        [contentEditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          cursor: text;
        }
        [contentEditable] {
          outline: none;
        }
        [contentEditable] * {
          margin-block-start: 0.5em;
          margin-block-end: 0.5em;
        }
        [contentEditable] h1 {
          font-size: 2em;
          font-weight: bold;
        }
        [contentEditable] h2 {
          font-size: 1.5em;
          font-weight: bold;
        }
        [contentEditable] h3 {
          font-size: 1.25em;
          font-weight: bold;
        }
        [contentEditable] a {
          color: #3b82f6;
          text-decoration: underline;
        }
        [contentEditable] ul, [contentEditable] ol {
          padding-left: 2em;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
